
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { hrefForUserProfile } from '@/utils/username';
import Image from '@/components/AppImage/AppImage';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useNotificationsBadge } from '@/contexts';
import { useAvatarImage } from '@/hooks/useAvatarImage';
import { useAuthAction } from '@/hooks/useAuthAction';
import { debug } from '@/utils/logger';
import '@/components/MobileTabBar/MobileTabBar.css';
import ImportInspoModal from '@/components/blocks/ImportInspoModal';
import CreateListModal from '@/components/CreateListModal/CreateListModal';
import CreateItineraryModal from '@/components/CreateItineraryModal/CreateItineraryModal';
import SignUpCTAPopover from '@/components/SignUpCTAPopover/SignUpCTAPopover';
import LoginModal from '@/components/LoginModal/LoginModal';
import SVGSpinner from '@/components/svgs/SVGSpinner';
import NotificationsPanel from '@/components/Notifications/Notifications';
import ComingSoonPopover from '@/components/ComingSoonPopover/ComingSoonPopover';
import GlobeThriversTermsContent from '@/components/Legal/GlobeThriversTermsContent';
import GlobeThriversPrivacyContent from '@/components/Legal/GlobeThriversPrivacyContent';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import AppVersionLabel from '@/components/AppVersionLabel/AppVersionLabel';
import AccountTypeSection from '@/components/EditProfile/AccountTypeSection';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import logoIcon from '@/assets/logo.svg';
import alarmBellIcon from '@/assets/alarm-bell.svg';
import messagesIcon from '@/assets/messages.svg';
import homeSearchIcon from '@/assets/home_search.png';
import arrowBackIcon from '@/assets/arrow-back.svg';
import menuIcon from '@/assets/images/menu.png';
import navSavedIcon from '@/assets/nav-item-saved.svg';
import navTripsIcon from '@/assets/nav-item-trips.svg';
import moreHotelBookingsIcon from '@/assets/hotel-icon.png';
import moreFaqIcon from '@/assets/images/faq_icon.png';
import moreTermsIcon from '@/assets/images/terms_conditions_icon.png';
import morePrivacyIcon from '@/assets/images/privacy_policy_icon.png';
import { getBackFallbackPath, getFromStatePath, isFromTabBar } from '@/utils/backNavigation';
import { useOpenInspoImportRoute } from '@/hooks/useOpenInspoImportRoute';
import { useOpenTripImportRoute } from '@/hooks/useOpenTripImportRoute';
import { useOpenAccountSettings } from '@/hooks/useOpenAccountSettings';
import { useTravelMenuActions } from '@/hooks/useTravelMenuActions';
import { isNativePlatform } from '@/lib/platform';
import { useBackHandler } from '@/lib/backHandlerRegistry';
import { useExploreHeaderExtension } from '@/contexts/ExploreHeaderExtensionContext';
import { OPEN_MOBILE_MORE_MENU_EVENT } from '@/constants/mobileHeaderRoutes';

// Community feature is not yet implemented; the mobile header icon currently
// only opens a "Coming Soon" popover, so we hide it on mobile until launch.
// The desktop pancake menu (Navigation.tsx) still surfaces Community.
// Flip this to true to re-enable the mobile header icon.
const COMMUNITY_MOBILE_ICON_ENABLED = false;

interface ExploreHeaderProps {
  variant?: 'transparent' | 'light';
  showMobileJourneyCta?: boolean;
  /** Hide the fixed top bar but keep the mobile "More" drawer mounted (own profile). */
  hideMobileBar?: boolean;
}

const ExploreHeader: React.FC<ExploreHeaderProps> = ({
  variant = 'transparent',
  showMobileJourneyCta = true,
  hideMobileBar = false,
}) => {
  const { extension: headerExtension } = useExploreHeaderExtension();
  const isLight = variant === 'light';
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { user, logout } = useAuth();
  const {
    handleBookClick,
    handleMyTripsClick,
    handleHotelBookingsClick,
    isBookWhitelabelLoading,
  } = useTravelMenuActions();
  const { unreadCount: notificationsUnreadCount } = useNotificationsBadge();
  const headerRef = useRef<HTMLElement>(null);
  const {
    requireAuth,
    showPopover,
    getPopoverProps,
    showLoginModal,
    setShowLoginModal,
    loginModalRedirectPath,
    handleSignUpClick,
  } = useAuthAction();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isImportInspoModalOpen, setIsImportInspoModalOpen] = useState(false);

  // Hardware back closes the profile dropdown instead of navigating away from the page.
  useBackHandler(() => setShowProfileMenu(false), showProfileMenu);
  // Paste-text entry from ImportInspoModal: when true, CreateListModal navigates to
  // Create list/trip from Start Building uses normal create flow (search addMode), not legacy paste import.
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [createListOpenAddMode, setCreateListOpenAddMode] = useState<'copy-paste' | 'search' | boolean>(false);
  const [showCreateItineraryModal, setShowCreateItineraryModal] = useState(false);
  const [createItineraryOpenAddMode, setCreateItineraryOpenAddMode] = useState<boolean | 'social' | 'copy-paste' | 'search'>(false);
  const [loginModalInitialMode, setLoginModalInitialMode] = useState<'login' | 'signup'>('signup');
  /** Desktop (lg+) and below-lg header both mount profile UI; use two refs for click-outside. */
  const profileMenuRefBelowLg = useRef<HTMLDivElement>(null);
  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false);
  const [isMobileMoreMenuClosing, setIsMobileMoreMenuClosing] = useState(false);
  // Held open while the Account Type dialog is up (see closeMobileMoreMenu).
  const [isAccountTypeDialogOpen, setIsAccountTypeDialogOpen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const isMobile = useIsMobileViewport();
  const openInspoImportRoute = useOpenInspoImportRoute();
  const openTripImportRoute = useOpenTripImportRoute();
  const openAccountSettings = useOpenAccountSettings();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCommunityPopover, setShowCommunityPopover] = useState(false);
  const showBackButton =
    isMobile &&
    (pathname.startsWith('/post/') ||
      pathname.startsWith('/list/') ||
      pathname.startsWith('/itinerary/') ||
      pathname.startsWith('/place/') ||
      pathname.startsWith('/recommendation/') ||
      (/^\/profile\/[^/]+\/?$/.test(pathname) && !isFromTabBar(location.state)));

  // Transparent variant pages (Explore, Creators, Destinations, etc.) render their own
  // scroll container beneath this fixed header instead of the shared <main>. Without a
  // background, content scrolls straight through the header row with nothing separating
  // chrome from content. Mirror iOS nav bars: stay transparent over the hero, then pick up
  // a solid backing + shadow as soon as the page scrolls, like a scrollEdgeAppearance swap.
  useEffect(() => {
    if (isLight) return;
    setIsScrolled(false);

    const handleScroll = (event: Event) => {
      const target = event.target;
      const scrollTop =
        target === document ? window.scrollY : (target as HTMLElement).scrollTop ?? 0;
      setIsScrolled(scrollTop > 4);
    };

    // Scroll events don't bubble, but they fire on window during the capture phase
    // for any scrollable descendant — lets one listener cover every page's own
    // internal scroll container without knowing which element it is.
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, [isLight, pathname]);

  // Mobile row's text/logo color stays keyed off `isLight` only — this fix targets the
  // lg+ (web) row, which doesn't use these two.
  const textColor = isLight ? 'text-gray-900' : 'text-white';
  const logoFilter = isLight ? '' : 'brightness-0 invert';
  // isLight pages stay solid on every breakpoint, same as before. Transparent-variant
  // pages stay transparent below lg (mobile hero) and only pick up the scroll-aware
  // solid backing at lg+.
  const headerBg = isLight
    ? 'bg-white shadow-surface lg:shadow-none'
    : isScrolled
      ? 'bg-transparent lg:bg-white'
      : 'bg-transparent';

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (profileMenuRefBelowLg.current?.contains(t)) {
        return;
      }
      setShowProfileMenu(false);
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  // Measure header height and publish to --gt-explore-header-inner so every
  // consumer (LayoutWrapper inset, Results viewport calc, screen-minus-header)
  // stays in sync automatically — no hardcoded rem values to maintain.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    let alive = true;

    const publishHeaderHeight = () => {
      if (!alive) return;
      const height = el.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty(
          '--gt-explore-header-inner',
          `${height}px`,
        );
      }
    };

    const observer = new ResizeObserver(([entry]) => {
      const observedHeight =
        entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect?.height ?? el.offsetHeight;
      if (observedHeight > 0) {
        document.documentElement.style.setProperty(
          '--gt-explore-header-inner',
          `${observedHeight}px`,
        );
      }
    });
    observer.observe(el);

    // WKWebView can occasionally miss ResizeObserver updates on fixed headers
    // during route transitions/animations. Proactively republish after mount.
    publishHeaderHeight();
    requestAnimationFrame(() => publishHeaderHeight());
    const timeoutId = window.setTimeout(publishHeaderHeight, 120);

    // Web fonts swapping in changes the row height; env(safe-area-inset-top) can resolve late on
    // real devices. Either leaves a stale (too small) inset, which lets the fixed header cover the
    // content directly beneath it — e.g. the profile Map View / Edit Profile row.
    document.fonts?.ready.then(publishHeaderHeight).catch(() => {});
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', publishHeaderHeight);

    return () => {
      alive = false;
      window.clearTimeout(timeoutId);
      viewport?.removeEventListener('resize', publishHeaderHeight);
      observer.disconnect();
    };
  }, [pathname, showMobileJourneyCta, isMobile, headerExtension]);

  const handleImportInspoClick = () => {
    requireAuth('post', () => {
      if (isMobile) {
        openInspoImportRoute();
        return;
      }
      setIsImportInspoModalOpen(true);
    }, {
      title: 'Sign up to import inspiration',
      description: 'Create an account to import posts and videos from Instagram and TikTok to turn your saved inspiration into trips.'
    });
  };

  const handleAvatarClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleMyProfileClick = () => {
    setShowProfileMenu(false);
    if (user?._id) {
      router(hrefForUserProfile({ _id: user._id, username: user.username, profile_type: user.profile_type }));
    }
  };

  const handleLogoutClick = async () => {
    setShowProfileMenu(false);
    try {
      await logout();
      router('/');
    } catch (err) {
      debug('[ExploreHeader] Logout error:', err);
    }
  };

  const handleMobileBookClick = () => {
    requireAuth('book', () => handleBookClick('header_mobile'));
  };

  const handleMobileCommunityClick = () => {
    if (!user) {
      requireAuth('community', () => {});
    } else {
      setShowCommunityPopover(true);
    }
  };

  const handleMobileNotificationsClick = () => {
    requireAuth('notifications', () => {
      setShowNotifications(true);
    });
  };

  const handleMoreMenuClick = () => {
    setShowMobileMoreMenu(true);
  };

  useEffect(() => {
    const open = () => setShowMobileMoreMenu(true);
    window.addEventListener(OPEN_MOBILE_MORE_MENU_EVENT, open);
    return () => window.removeEventListener(OPEN_MOBILE_MORE_MENU_EVENT, open);
  }, []);

  const closeMobileMoreMenu = () => {
    // The Account Type dialog renders inside this drawer, so dismissing the
    // drawer would unmount it mid-interaction.
    if (isAccountTypeDialogOpen) return;
    setIsMobileMoreMenuClosing(true);
  };

  const handleMobileMoreMenuAnimationEnd = () => {
    if (isMobileMoreMenuClosing) {
      setShowMobileMoreMenu(false);
      setIsMobileMoreMenuClosing(false);
    }
  };

  const handleMobileSavedClick = () => {
    closeMobileMoreMenu();
    router('/saved');
  };

  const handleMobileMyTripsClick = () => {
    closeMobileMoreMenu();
    handleMyTripsClick();
  };

  const handleMobileHotelBookingsClick = () => {
    closeMobileMoreMenu();
    handleHotelBookingsClick();
  };

  const handleMobileAccountSettingsClick = () => {
    closeMobileMoreMenu();
    openAccountSettings('nav_more_mobile');
  };

  const handleMobileFaqClick = () => {
    closeMobileMoreMenu();
    router('/faq');
  };

  const handleMobileTermsClick = () => {
    closeMobileMoreMenu();
    setShowTermsModal(true);
  };

  const handleMobilePrivacyClick = () => {
    closeMobileMoreMenu();
    setShowPrivacyModal(true);
  };

  const handleMobileUpgradeClick = () => {
    closeMobileMoreMenu();
    router('/advisor-pro');
  };

  const handleMobileLogoutClick = async () => {
    closeMobileMoreMenu();
    try {
      await logout();
      router('/');
    } catch (err) {
      debug('[ExploreHeader] Logout error:', err);
    }
  };

  // Hardware back closes the mobile "More" drawer instead of navigating away from the page.
  useBackHandler(closeMobileMoreMenu, showMobileMoreMenu && !isMobileMoreMenuClosing);

  const handleBackClick = () => {
    const currentPathWithQuery = `${location.pathname}${location.search}`;
    const fromPath = getFromStatePath(location.state, currentPathWithQuery);
    const fallbackPath = getBackFallbackPath(pathname);

    if (fromPath) {
      router(fromPath, { replace: true });
      return;
    }

    // iOS WKWebView can occasionally restore a broken intermediate history
    // entry after detail-route pops, leaving shell chrome visible but content
    // blank until app restart. Prefer deterministic route fallbacks on native
    // detail pages instead of history pop.
    const avoidHistoryPop =
      isNativePlatform() &&
      (pathname.startsWith('/itinerary/') ||
        pathname.startsWith('/list/') ||
        pathname.startsWith('/post/') ||
        pathname.startsWith('/place/') ||
        pathname.startsWith('/recommendation/') ||
        /^\/profile\/[^/]+\/?$/.test(pathname));

    if (!avoidHistoryPop && typeof window !== 'undefined' && window.history.length > 1) {
      router(-1);
      return;
    }
    router(fallbackPath, { replace: true });
  };

  const resolvedAvatarImage = useAvatarImage({
    largeAvatar: typeof user?.large_avatar_url === 'string' ? user.large_avatar_url : undefined,
    smallAvatar: typeof user?.small_avatar_url === 'string' ? user.small_avatar_url : undefined,
    avatarUrl: typeof user?.avatar_url === 'string' ? user.avatar_url : undefined,
    profileImageUrl: typeof user?.profile_image_url === 'string' ? user.profile_image_url : undefined,
  }) as string;

  const profileTypeLabel =
    user?.profile_type === 'creator' ? 'Creator'
      : user?.profile_type === 'advisor' ? 'Advisor'
        : 'Traveler';

  return (
    <>
    <header
      ref={headerRef}
      hidden={hideMobileBar}
      className={`fixed top-0 left-0 right-0 w-full z-header ${headerBg}`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Desktop (lg+): login (logged out only). Explore/My Trips/Search/Book/Create/
          Profile now live in the left icon rail (Navigation.tsx), so this row no
          longer duplicates them. The fixed header spans the full viewport
          (left-0 right-0), so lg:pl-[104px] reserves the 80px rail width + the
          same lg:px-6 gutter the hero/content sections use.
          explore-header-search-row is targeted by LayoutWrapper.css to push this
          row over (instead of letting a rail flyout cover it) in sync with .app-main. */}
      {!user && (
        <div className="explore-header-search-row hidden lg:flex items-center justify-end gap-4 p-4 lg:pl-[104px] lg:pr-6 max-w-full mx-auto w-full">
          <button
            type="button"
            className="flex-shrink-0 border-none text-body font-semibold font-manrope cursor-pointer py-3 px-6 rounded-button transition-all whitespace-nowrap bg-gray-900 text-white hover:bg-gray-700"
            onClick={() => { setLoginModalInitialMode('login'); handleSignUpClick(pathname || '/'); }}
          >
            Log In
          </button>
        </div>
      )}

      {headerExtension ? (
        <div className="hidden lg:block w-full max-w-full mx-auto px-4 md:px-8">
          {headerExtension}
        </div>
      ) : null}

      {/* Below lg: row 1 = logo + actions; optional row 2 = headline + Import Travel Inspo CTA.
          Tight vertical rhythm keeps the header compact on native where
          env(safe-area-inset-top) already adds ~47-59 px above. */}
      {/* md:pl reserves the left rail's 80px width at tablet widths (768-1023px),
          where the rail is present but this row (not the lg+ row) still renders. */}
      <div className="explore-header-search-row lg:hidden flex flex-col gap-1.5 pt-1.5 pb-2.5 px-4 md:pl-[104px] md:pr-8 max-w-full mx-auto w-full">
        <div className="flex justify-between items-center gap-3 min-w-0">
          <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
            {showBackButton && (
              <button
                type="button"
                className={`bg-transparent border-none cursor-pointer p-0 flex items-center justify-center ${textColor} transition-opacity hover:opacity-80`}
                onClick={handleBackClick}
                aria-label="Go back"
              >
                <img src={arrowBackIcon} alt="" width={22} height={22} className={logoFilter} />
              </button>
            )}
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => router('/')}
            >
            <Image
              src={logoIcon}
              alt="Globe Thrivers"
              width={150}
              height={40}
              className={`h-8 w-auto ${logoFilter}`}
              priority
            />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 md:gap-4 flex-1 min-w-0 flex-shrink-0">
            {user ? (
              <div className="relative flex-shrink-0 hidden md:flex" ref={profileMenuRefBelowLg}>
                <button
                  type="button"
                  className="flex items-center justify-center bg-transparent border-none cursor-pointer p-0 rounded-full overflow-hidden transition-transform hover:scale-105 flex-shrink-0"
                  onClick={handleAvatarClick}
                  aria-label="Profile menu"
                  aria-expanded={showProfileMenu}
                  aria-haspopup="true"
                >
                  <Image
                    src={resolvedAvatarImage}
                    alt={user.name || 'User'}
                    className="rounded-full w-10 h-10 object-cover"
                    width={40}
                    height={40}
                  />
                </button>

                {showProfileMenu && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-tag shadow-card-drop z-dropdown overflow-hidden">
                    <button
                      type="button"
                      className="w-full p-4 bg-transparent border-none text-left font-manrope font-normal text-body leading-[1.366] text-gray-700 cursor-pointer transition-colors hover:bg-black/5"
                      onClick={handleMyProfileClick}
                    >
                      My Profile
                    </button>
                    <div className="h-px bg-gray-200" />
                    <button
                      type="button"
                      className="w-full p-4 bg-transparent border-none text-left font-manrope font-normal text-body leading-[1.366] text-gray-700 cursor-pointer transition-colors hover:bg-black/5"
                      onClick={handleLogoutClick}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className={`border-none text-body font-semibold font-manrope cursor-pointer py-3 px-6 rounded-button transition-all whitespace-nowrap ${isLight ? 'bg-gray-900 text-white hover:bg-gray-700' : 'bg-white/20 text-white hover:bg-white/30'}`}
                onClick={() => { setLoginModalInitialMode('login'); handleSignUpClick(pathname || '/'); }}
              >
                Log In
              </button>
            )}

            {isMobile ? (
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleMobileBookClick}
                  disabled={isBookWhitelabelLoading}
                  data-testid="header-mobile-book-button"
                  aria-label="Book"
                  title={isBookWhitelabelLoading ? 'Opening booking…' : undefined}
                  className="inline-flex items-center justify-center gap-1.5 rounded-[100px] bg-[#f3f4f6] px-4 py-1.5 font-manrope text-[13px] font-semibold leading-normal text-gray-900 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBookWhitelabelLoading && <SVGSpinner />}
                  {isBookWhitelabelLoading ? 'Opening…' : 'Book'}
                </button>
                {COMMUNITY_MOBILE_ICON_ENABLED && (
                  <button
                    type="button"
                    className={`bg-transparent border-none cursor-pointer p-0 flex items-center justify-center ${textColor} transition-opacity hover:opacity-80`}
                    onClick={handleMobileCommunityClick}
                    aria-label="Community"
                  >
                    <img src={messagesIcon} alt="Community" width={22} height={22} className={logoFilter} />
                  </button>
                )}
                {user && (
                  <button
                    type="button"
                    className={`bg-transparent border-none cursor-pointer p-0 flex items-center justify-center ${textColor} transition-opacity hover:opacity-80 relative`}
                    onClick={handleMobileNotificationsClick}
                    aria-label="Notifications"
                  >
                    <img src={alarmBellIcon} alt="Notifications" width={22} height={22} className={logoFilter} />
                    {notificationsUnreadCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
                        aria-label={`${notificationsUnreadCount} unread notifications`}
                      >
                        {notificationsUnreadCount > 99 ? '99+' : notificationsUnreadCount}
                      </span>
                    )}
                  </button>
                )}
                {user && (
                  <button
                    type="button"
                    className="bg-transparent border-none cursor-pointer p-0 flex items-center justify-center transition-opacity hover:opacity-80 flex-shrink-0"
                    onClick={handleMoreMenuClick}
                    aria-label="More"
                    aria-expanded={showMobileMoreMenu}
                    aria-haspopup="true"
                  >
                    <img src={menuIcon} alt="" width={32} height={32} className={logoFilter} />
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {headerExtension ? (
          <div className="w-full pt-1">{headerExtension}</div>
        ) : null}
        {showMobileJourneyCta && !headerExtension && (
          <div className="flex flex-col items-center gap-[var(--gt-mobile-header-rhythm)] w-full">
            <p
              className={`font-lora text-body-lg font-semibold text-center ${isLight ? 'text-gray-900' : 'text-white'}`}
            >
              Let's plan your next adventure.
            </p>
            {/* Gradient border — full width within header padding (matches RN: row + viewPadding + fullWidth flex:1) */}
            <div className="p-[2px] rounded-button-md w-full shadow-card bg-[linear-gradient(135deg,#e3f0ad,#d6ccff,#ffc4a2)]">
              <button
                onClick={handleImportInspoClick}
                className="w-full flex items-center justify-center gap-2 bg-white rounded-[10px] h-button-height px-3 font-manrope text-body font-bold text-gray-900 tracking-body"
              >
                <img src={homeSearchIcon} alt="" className="w-8 h-8 object-contain" />
                Import Travel Inspo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create / import-inspo modal */}
      <ImportInspoModal
        isOpen={isImportInspoModalOpen}
        onClose={() => setIsImportInspoModalOpen(false)}
        onCreateListClick={() => {
          setIsImportInspoModalOpen(false);
          setCreateListOpenAddMode('search');
          setShowCreateListModal(true);
        }}
        onCreateTripClick={() => {
          setIsImportInspoModalOpen(false);
          setCreateItineraryOpenAddMode('search');
          setShowCreateItineraryModal(true);
        }}
        onSocialImportClick={() => {
          openInspoImportRoute();
        }}
        onTripImportClick={() => {
          openTripImportRoute({ entry: 'start_building' });
        }}
      />

      {/* Auth popover for logged-out users */}
      {showPopover && getPopoverProps() && (
        <SignUpCTAPopover
          {...getPopoverProps()!}
          onSignUpClick={() => handleSignUpClick('/')}
        />
      )}

      {/* Login modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => { setShowLoginModal(false); setLoginModalInitialMode('signup'); }}
          redirectPath={loginModalRedirectPath || '/'}
          initialMode={loginModalInitialMode}
        />
      )}

      {/* Mobile-only: Notifications panel */}
      {isMobile && (
        <NotificationsPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          entryPoint="header_mobile"
        />
      )}

      {/* Mobile-only: Community popover */}
      {isMobile && showCommunityPopover && (
        <ComingSoonPopover
          onClose={() => setShowCommunityPopover(false)}
          featureType="community"
        />
      )}
    </header>
      {/* Mobile-only: "More" slide-in drawer (profile header + Saved Inspo/My Travel/Hotel
          Bookings/Account Settings/Help & FAQs/Terms/Privacy/Log out) */}
      {isMobile && showMobileMoreMenu && user && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <>
              <div
                className={`mobile-more-drawer-overlay${isMobileMoreMenuClosing ? ' mobile-more-drawer-overlay--closing' : ''}`}
                onClick={closeMobileMoreMenu}
              />
              <div
                className={`mobile-more-drawer${isMobileMoreMenuClosing ? ' mobile-more-drawer--closing' : ''}`}
                role="menu"
                aria-label="More"
                onAnimationEnd={handleMobileMoreMenuAnimationEnd}
              >
                <div className="mobile-more-drawer-header">
                  <Image
                    src={resolvedAvatarImage}
                    alt={user.name || 'User'}
                    className="mobile-more-drawer-avatar"
                    width={32}
                    height={32}
                  />
                  <div className="mobile-more-drawer-identity">
                    <span className="mobile-more-drawer-name">{user.name || 'My Account'}</span>
                    <span className="mobile-more-drawer-role">{profileTypeLabel}</span>
                  </div>
                </div>
                <div className="mobile-more-drawer-divider" style={{ marginBottom: 0 }} />

                <button type="button" className="mobile-more-drawer-item" onClick={handleMobileSavedClick}>
                  <img src={navSavedIcon} alt="" className="mobile-more-drawer-item-icon mobile-more-drawer-item-icon--rail" />
                  Saved Inspo
                </button>
                <button type="button" className="mobile-more-drawer-item" onClick={handleMobileMyTripsClick}>
                  <img src={navTripsIcon} alt="" className="mobile-more-drawer-item-icon mobile-more-drawer-item-icon--rail" />
                  My Trips
                </button>
                <button type="button" className="mobile-more-drawer-item" onClick={handleMobileHotelBookingsClick}>
                  <img src={moreHotelBookingsIcon} alt="" className="mobile-more-drawer-item-icon" />
                  Hotel Bookings
                </button>

                <div className="mobile-more-drawer-divider" style={{ marginBottom: 0 }} />

                <button type="button" className="mobile-more-drawer-item" onClick={handleMobileAccountSettingsClick}>
                  <ManageAccountsOutlinedIcon sx={{ fontSize: 22, color: '#252528' }} className="mobile-more-drawer-item-icon" aria-hidden />
                  Account Settings
                </button>
                <button type="button" className="mobile-more-drawer-item" onClick={handleMobileFaqClick}>
                  <img src={moreFaqIcon} alt="" className="mobile-more-drawer-item-icon" />
                  Help &amp; FAQs
                </button>
                <button type="button" className="mobile-more-drawer-item" onClick={handleMobileTermsClick}>
                  <img src={moreTermsIcon} alt="" className="mobile-more-drawer-item-icon" />
                  Terms &amp; Conditions
                </button>
                <button type="button" className="mobile-more-drawer-item" onClick={handleMobilePrivacyClick}>
                  <img src={morePrivacyIcon} alt="" className="mobile-more-drawer-item-icon" />
                  Privacy Policy
                </button>

                <div className="mobile-more-drawer-spacer" />

                {/* Advisor Pro upsell — mobile entry point into the upgrade flow. */}
                {user.profile_type === 'advisor' && (
                  <div className="mobile-more-drawer-upgrade">
                    <span className="mobile-more-drawer-upgrade__accent" />
                    <span className="mobile-more-drawer-upgrade__title">Advisor Pro</span>
                    <p className="mobile-more-drawer-upgrade__description">
                      Get verified, sell your services, earn from custom itineraries and all
                      bookings, and featured placement.
                    </p>
                    <button
                      type="button"
                      className="mobile-more-drawer-upgrade__button"
                      onClick={handleMobileUpgradeClick}
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}

                <div className="mobile-more-drawer-divider" />

                <div style={{ padding: '0 20px', marginTop: 12, marginBottom: 2 }}>
                  <AccountTypeSection onOverlayOpenChange={setIsAccountTypeDialogOpen} />
                </div>

                <button
                  type="button"
                  className="mobile-more-drawer-item mobile-more-drawer-item--destructive"
                  onClick={handleMobileLogoutClick}
                >
                  Log out
                </button>
                <AppVersionLabel className="mobile-more-drawer-version" />
                <div style={{ height: 16 }} />
              </div>
            </>,
            document.body,
          )
        : null}

      {/* Mobile-only: Terms & Privacy modals opened from the "More" drawer */}
      {isMobile && showTermsModal && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
              onClick={() => setShowTermsModal(false)}
            >
              <div
                className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-lora text-xl font-semibold text-gray-900">Terms &amp; Conditions</h3>
                  <button
                    type="button"
                    className="text-2xl leading-none text-gray-500 hover:text-gray-900"
                    onClick={() => setShowTermsModal(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="font-manrope text-[14px] leading-[1.5] text-gray-700 [&_h4]:mt-4 [&_h4]:font-semibold [&_h4]:text-gray-900 [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5">
                  <GlobeThriversTermsContent />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
      {isMobile && showPrivacyModal && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
              onClick={() => setShowPrivacyModal(false)}
            >
              <div
                className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-lora text-xl font-semibold text-gray-900">Privacy Policy</h3>
                  <button
                    type="button"
                    className="text-2xl leading-none text-gray-500 hover:text-gray-900"
                    onClick={() => setShowPrivacyModal(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <div className="font-manrope text-[14px] leading-[1.5] text-gray-700 [&_h4]:mt-4 [&_h4]:font-semibold [&_h4]:text-gray-900 [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5">
                  <GlobeThriversPrivacyContent />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
      {/* Portaled outside fixed header — avoids Safari desktop stacking under ExploreHeader */}
      {showCreateListModal && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <CreateListModal
              onClose={() => {
                setShowCreateListModal(false);
                setCreateListOpenAddMode(false);
              }}
              onListCreated={() => {
                setShowCreateListModal(false);
                setCreateListOpenAddMode(false);
              }}
              openAddModeOnCreate={createListOpenAddMode}
            />,
            document.body,
          )
        : null}
      {showCreateItineraryModal && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <CreateItineraryModal
              onClose={() => {
                setShowCreateItineraryModal(false);
                setCreateItineraryOpenAddMode(false);
              }}
              onTripCreated={() => {
                setShowCreateItineraryModal(false);
                setCreateItineraryOpenAddMode(false);
              }}
              openAddModeOnCreate={createItineraryOpenAddMode}
            />,
            document.body,
          )
        : null}
    </>
  );
};

export default ExploreHeader;
